import { useMemo } from "react";
import type { ActiveFilter } from "../components/DataFilters/DataFilters";
import type { useUrlParams } from "./useUrlParams";

type UseFiltersOptions<T extends Record<string, string | boolean>> = {
  filters: T;
  setFilters: React.Dispatch<React.SetStateAction<T>>;
  updateUrl: ReturnType<typeof useUrlParams>["updateUrl"];
  labelOverrides?: Partial<Record<keyof T, string>>;
};

export function useFilters<T extends Record<string, string | boolean>>({
  filters,
  setFilters,
  updateUrl,
  labelOverrides = {},
}: UseFiltersOptions<T>) {
  const activeFilters: ActiveFilter[] = useMemo(() => {
    const order: (keyof T)[] = ["edition", "publication", "region"] as (keyof T)[];

    const entries = Object.entries(filters)
      .filter(([_, value]) => value !== "all" && value !== false)
      .map(([key, value]) => {
        const label = labelOverrides[key as keyof T] || key.charAt(0).toUpperCase() + key.slice(1);

        return {
          key,
          label,
          value: String(value),
        };
      });

    const sorted = entries.sort((a, b) => {
      const aIndex = order.indexOf(a.key as keyof T);
      const bIndex = order.indexOf(b.key as keyof T);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return sorted;
  }, [filters, labelOverrides]);

  const handleFilterChange = (
    key: string,
    value: string | number | boolean,
    additionalUrlParams?: Record<string, string>,
  ) => {
    const stringValue = String(value);
    setFilters(prev => ({
      ...prev,
      [key]: typeof filters[key as keyof T] === "boolean" ? value : stringValue,
    }));
    updateUrl({ [key]: stringValue, ...additionalUrlParams });
  };

  const handleClearFilter = (key: string, additionalUrlParams?: Record<string, string>) => {
    if (typeof filters[key as keyof T] === "boolean") {
      handleFilterChange(key, false, additionalUrlParams);
    } else {
      handleFilterChange(key, "all", additionalUrlParams);
    }
  };

  return {
    activeFilters,
    handleFilterChange,
    handleClearFilter,
  };
}
